import { useState } from 'react';

import { ChevronDown, ChevronRight, MoreHorizontal, Plus, Trash2, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

export interface ReviewFieldDefinition {
  name: string;
  type: 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object';
  description: string;
  required?: boolean;
  nullable?: boolean;
  enum?: Array<string | number>;
  // For arrays
  items?: {
    type: 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array';
    properties?: ReviewFieldDefinition[];
    required?: string[];
    enum?: Array<string | number>;
  };
  // For objects
  properties?: ReviewFieldDefinition[];
  requiredFields?: string[]; // For nested objects
}

const JSON_SCHEMA_TYPE_OPTIONS = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'integer', label: 'Integer' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
] as const;

interface ReviewFieldEditorProps {
  field: ReviewFieldDefinition;
  onChange: (field: ReviewFieldDefinition) => void;
  onRemove: () => void;
  level?: number;
  index?: number;
  highIntentEmails?: string[];
  onHighIntentEmailsChange?: (emails: string[]) => void;
}

export const ReviewFieldEditor = ({
  field,
  onChange,
  onRemove,
  level = 0,
  index,
  highIntentEmails,
  onHighIntentEmailsChange,
}: ReviewFieldEditorProps) => {
  const [isExpanded, setIsExpanded] = useState(level === 0);
  const [showAdvanced, setShowAdvanced] = useState(
    !!(field.enum && field.enum.length > 0) ||
      (field.name === 'should_send_high_intent_email' && level === 0),
  );
  const [enumInput, setEnumInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');

  const isHighIntentField = field.name === 'should_send_high_intent_email';

  const addEmail = () => {
    setEmailError('');
    const email = emailInput.trim();
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Invalid email format');
      return;
    }
    if (highIntentEmails?.includes(email)) {
      setEmailError('Email already added');
      return;
    }
    onHighIntentEmailsChange?.([...(highIntentEmails || []), email]);
    setEmailInput('');
  };

  const removeEmail = (emailToRemove: string) => {
    onHighIntentEmailsChange?.((highIntentEmails || []).filter((e) => e !== emailToRemove));
  };

  const updateField = <K extends keyof ReviewFieldDefinition>(
    fieldKey: K,
    value: ReviewFieldDefinition[K],
  ) => {
    onChange({ ...field, [fieldKey]: value });
  };

  const addNestedProperty = () => {
    const newProp: ReviewFieldDefinition = {
      name: '',
      type: 'string',
      description: '',
      required: false,
      nullable: false,
    };

    if (field.type === 'array') {
      const currentItems = field.items || { type: 'object', properties: [] };
      const currentProps = currentItems.properties || [];
      onChange({
        ...field,
        items: {
          ...currentItems,
          type: 'object',
          properties: [...currentProps, newProp],
        },
      });
    } else if (field.type === 'object') {
      const currentProps = field.properties || [];
      onChange({
        ...field,
        properties: [...currentProps, newProp],
      });
    }
  };

  const removeNestedProperty = (idx: number) => {
    if (field.type === 'array' && field.items?.properties) {
      const updatedProps = field.items.properties.filter((_, i) => i !== idx);
      const updatedRequired = updatedProps.filter((p) => p.name && p.required).map((p) => p.name);
      onChange({
        ...field,
        items: {
          ...field.items,
          properties: updatedProps,
          required: updatedRequired.length > 0 ? updatedRequired : undefined,
        },
      });
    } else if (field.type === 'object' && field.properties) {
      const updatedProps = field.properties.filter((_, i) => i !== idx);
      const updatedRequired = updatedProps.filter((p) => p.name && p.required).map((p) => p.name);
      onChange({
        ...field,
        properties: updatedProps,
        requiredFields: updatedRequired.length > 0 ? updatedRequired : undefined,
      });
    }
  };

  const updateNestedProperty = (idx: number, updatedProp: ReviewFieldDefinition) => {
    if (field.type === 'array' && field.items?.properties) {
      const updatedProps = [...field.items.properties];
      updatedProps[idx] = updatedProp;
      const updatedRequired = updatedProps.filter((p) => p.name && p.required).map((p) => p.name);
      onChange({
        ...field,
        items: {
          ...field.items,
          properties: updatedProps,
          required: updatedRequired.length > 0 ? updatedRequired : undefined,
        },
      });
    } else if (field.type === 'object' && field.properties) {
      const updatedProps = [...field.properties];
      updatedProps[idx] = updatedProp;
      const updatedRequired = updatedProps.filter((p) => p.name && p.required).map((p) => p.name);
      onChange({
        ...field,
        properties: updatedProps,
        requiredFields: updatedRequired.length > 0 ? updatedRequired : undefined,
      });
    }
  };

  const addEnumValue = () => {
    const value = enumInput.trim();
    if (!value) return;

    let parsedValue: string | number = value;
    if ((field.type === 'number' || field.type === 'integer') && !isNaN(Number(value))) {
      parsedValue = Number(value);
    }

    const currentEnum = field.enum || [];
    if (!currentEnum.includes(parsedValue)) {
      updateField('enum', [...currentEnum, parsedValue]);
      setEnumInput('');
    }
  };

  const removeEnumValue = (valueToRemove: string | number) => {
    const currentEnum = field.enum || [];
    updateField(
      'enum',
      currentEnum.filter((v) => v !== valueToRemove),
    );
  };

  const handleTypeChange = (newType: ReviewFieldDefinition['type']) => {
    const updates: Partial<ReviewFieldDefinition> = { type: newType };
    if (newType === 'array') {
      updates.items = { type: 'object', properties: [] };
      updates.enum = undefined;
    } else if (newType === 'object') {
      updates.properties = [];
      updates.enum = undefined;
    } else {
      updates.items = undefined;
      updates.properties = undefined;
    }
    onChange({ ...field, ...updates });
  };

  const hasNesting = field.type === 'array' || field.type === 'object';
  const nestedProperties = field.type === 'array' ? field.items?.properties : field.properties;
  const nestedCount = nestedProperties?.length ?? 0;
  const supportsEnum =
    field.type === 'string' || field.type === 'number' || field.type === 'integer';
  const hasEnumValues = field.enum && field.enum.length > 0;
  const hasAdvancedContent =
    supportsEnum ||
    (field.type === 'array' && !field.items?.properties) ||
    (isHighIntentField && level === 0);
  return (
    <div
      className={level > 0 ? 'border-primary/10 ml-4 border-l-2 pl-3' : ''}
      role="group"
      aria-label={field.name ? `Field: ${field.name}` : `Field ${(index ?? 0) + 1}`}
    >
      <div className="group/field hover:border-primary/20 overflow-hidden rounded-lg border transition-colors">
        {/* Row 1: Field identity + controls */}
        <div className="flex items-center gap-1.5 px-3 py-1.5">
          {hasNesting ? (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-muted-foreground hover:text-foreground -ml-1 shrink-0 rounded p-0.5 transition-colors"
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${field.name || 'field'}`}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <div className="w-4 shrink-0" />
          )}

          {/* Field name */}
          <input
            type="text"
            placeholder="field_name"
            value={field.name}
            onChange={(e) => updateField('name', e.target.value)}
            className="placeholder:text-muted-foreground/30 min-w-0 flex-1 truncate bg-transparent font-mono text-sm outline-none"
            aria-label="Field name"
          />

          {/* Type select */}
          <select
            value={field.type}
            onChange={(e) => handleTypeChange(e.target.value as ReviewFieldDefinition['type'])}
            className="border-input bg-background focus:ring-primary h-7 shrink-0 cursor-pointer rounded-md border px-1.5 pr-6 text-xs font-medium outline-none focus:ring-1"
            aria-label={`Type for ${field.name || 'field'}`}
          >
            {JSON_SCHEMA_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <div className="bg-border h-4 w-px shrink-0" aria-hidden="true" />

          {/* Required toggle */}
          <label className="flex shrink-0 cursor-pointer items-center gap-1.5 select-none">
            <Checkbox
              checked={field.required ?? false}
              onCheckedChange={(checked) => updateField('required', !!checked)}
              aria-label={`${field.name || 'Field'} is required`}
              className="h-3.5 w-3.5"
            />
            <span className="text-muted-foreground text-xs">Required</span>
          </label>

          <label className="flex shrink-0 cursor-pointer items-center gap-1.5 select-none">
            <Checkbox
              checked={field.nullable ?? false}
              onCheckedChange={(checked) => updateField('nullable', !!checked)}
              aria-label={`${field.name || 'Field'} is nullable`}
              className="h-3.5 w-3.5"
            />
            <span className="text-muted-foreground text-xs">Nullable</span>
          </label>

          {/* Nested count */}
          {hasNesting && nestedCount > 0 && (
            <>
              <div className="bg-border h-4 w-px shrink-0" aria-hidden="true" />
              <span className="text-muted-foreground shrink-0 text-[11px]">
                {nestedCount} {nestedCount === 1 ? 'prop' : 'props'}
              </span>
            </>
          )}

          {/* Advanced toggle */}
          {hasAdvancedContent && (
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`shrink-0 rounded p-1 transition-colors ${
                showAdvanced
                  ? 'text-primary bg-primary/10'
                  : 'group-hover/field:text-muted-foreground/40 hover:!text-muted-foreground text-transparent'
              }`}
              aria-expanded={showAdvanced}
              aria-label="Toggle advanced options"
              title="Advanced options"
            >
              <MoreHorizontal size={14} />
            </button>
          )}

          {/* Delete */}
          <button
            type="button"
            onClick={onRemove}
            className="hover:!bg-destructive/10 hover:!text-destructive group-hover/field:text-muted-foreground/40 shrink-0 rounded p-1 text-transparent transition-colors"
            aria-label={`Remove ${field.name || 'field'}`}
          >
            <Trash2 size={13} />
          </button>
        </div>

        {/* Row 2: Description — always visible, compact */}
        <div className="px-3 pb-2 pl-[30px]">
          <input
            type="text"
            placeholder="Add description..."
            value={field.description}
            onChange={(e) => updateField('description', e.target.value)}
            className="text-muted-foreground placeholder:text-muted-foreground/25 focus:placeholder:text-muted-foreground/40 w-full bg-transparent text-xs outline-none"
            aria-label={`Description for ${field.name || 'field'}`}
          />
        </div>

        {/* Enum badges — always visible when values exist */}
        {hasEnumValues && (
          <div className="flex flex-wrap items-center gap-1 border-t px-3 py-1.5 pl-[30px]">
            <span className="text-muted-foreground/60 mr-0.5 text-[10px] font-semibold tracking-wider uppercase">
              Enum
            </span>
            {field.enum?.map((value, idx) => (
              <span
                key={idx}
                className="bg-primary/8 text-primary inline-flex items-center gap-0.5 rounded-full px-2 py-px text-[11px] font-medium"
              >
                {String(value)}
                <button
                  type="button"
                  onClick={() => removeEnumValue(value)}
                  className="hover:text-destructive ml-0.5 opacity-50 transition-opacity hover:opacity-100"
                  aria-label={`Remove ${value}`}
                >
                  <X size={10} />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Advanced section — toggled by ⋯ button */}
        {showAdvanced && (
          <div className="bg-muted/20 space-y-3 border-t px-3 py-2.5 pl-[30px]">
            {/* Enum editor */}
            {supportsEnum && (
              <div className="space-y-1.5">
                <label className="text-muted-foreground text-[11px] font-medium">Enum values</label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    placeholder="Add value..."
                    value={enumInput}
                    onChange={(e) => setEnumInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addEnumValue();
                      }
                    }}
                    className="border-input bg-background focus:ring-primary h-7 flex-1 rounded-md border px-2 text-xs outline-none focus:ring-1"
                    aria-label="New enum value"
                  />
                  <button
                    type="button"
                    onClick={addEnumValue}
                    className="text-muted-foreground hover:border-primary hover:text-primary flex h-7 items-center gap-0.5 rounded-md border px-2 text-xs transition-colors"
                    aria-label="Add enum value"
                  >
                    <Plus size={12} />
                    Add
                  </button>
                </div>
              </div>
            )}

            {/* Array item type */}
            {field.type === 'array' && !field.items?.properties && (
              <div className="space-y-1.5">
                <label className="text-muted-foreground text-[11px] font-medium">
                  Array item type
                </label>
                <div className="relative w-40">
                  <select
                    value={field.items?.type || 'string'}
                    onChange={(e) => {
                      const itemType = e.target.value as ReviewFieldDefinition['type'];
                      updateField('items', {
                        type: itemType,
                        ...(itemType === 'object' ? { properties: [] } : {}),
                      });
                    }}
                    className="border-input bg-background focus:ring-primary h-7 w-full cursor-pointer appearance-none rounded-md border pr-6 pl-2 text-xs outline-none focus:ring-1"
                    aria-label="Array item type"
                  >
                    {JSON_SCHEMA_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={10}
                    className="text-muted-foreground pointer-events-none absolute top-1/2 right-2 -translate-y-1/2"
                    aria-hidden="true"
                  />
                </div>
              </div>
            )}

            {/* High Intent Emails */}
            {isHighIntentField && level === 0 && (
              <div className="space-y-2 rounded-md border border-amber-500/20 bg-amber-500/5 p-3">
                <label className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                  High Intent Notification Emails
                </label>
                <p className="text-muted-foreground text-xs">
                  These emails will receive an alert when high buying intent is detected.
                </p>
                <div className="mt-1 flex gap-2">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addEmail();
                      }
                    }}
                    placeholder="sales@example.com"
                    aria-label="High intent notification email"
                    className="border-input bg-background placeholder:text-muted-foreground flex h-8 w-full rounded-md border px-3 py-1 text-sm focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none"
                  />
                  <Button
                    type="button"
                    onClick={addEmail}
                    size="sm"
                    className="h-8 shrink-0 bg-amber-600 text-white shadow-sm hover:bg-amber-700"
                  >
                    Add
                  </Button>
                </div>
                {emailError && <p className="text-destructive mt-1 text-xs">{emailError}</p>}
                {highIntentEmails && highIntentEmails.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {highIntentEmails.map((email) => (
                      <Badge
                        key={email}
                        variant="secondary"
                        className="gap-1 border-amber-500/20 bg-amber-500/10 py-0.5 pr-1 text-amber-700 hover:bg-amber-500/20 dark:text-amber-300"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => removeEmail(email)}
                          className="hover:text-destructive rounded-full p-0.5 transition-colors"
                          aria-label={`Remove ${email}`}
                        >
                          <X size={12} />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Nested properties */}
        {isExpanded && hasNesting && (
          <div className="border-t px-3 py-2.5">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-muted-foreground text-[11px] font-medium">
                {field.type === 'array' ? 'Array item properties' : 'Object properties'}
              </span>
              <Button
                type="button"
                onClick={addNestedProperty}
                size="sm"
                variant="outline"
                className="h-6 text-xs"
              >
                <Plus size={12} className="mr-1" />
                Add Property
              </Button>
            </div>

            {nestedProperties && nestedProperties.length > 0 ? (
              <div className="space-y-1.5">
                {nestedProperties.map((nestedProp, idx) => (
                  <ReviewFieldEditor
                    key={idx}
                    field={nestedProp}
                    onChange={(updated) => updateNestedProperty(idx, updated)}
                    onRemove={() => removeNestedProperty(idx)}
                    level={level + 1}
                    index={idx}
                  />
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground py-2 text-center text-xs italic">
                No properties defined. Click &quot;Add Property&quot; to add nested fields.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
